import { useCallback } from 'react';
import { Content } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { Tooltip } from 'src/core/ui';
import { HoverableIcon } from 'src/core/ui/HoverableIcon/HoverableIcon';
import { PurchaseVendorAccountDialog } from 'src/dialogs/PurchaseVendorAccountDialog';
import { useAddVendorAccountActions } from 'src/navigations/Routes/Settings/Views/VendorManagement/Vendors/components/VendorActions/useAddVendorAccountActions';
import { VendorAccountHealthIcon } from 'src/svgs/VendorAccountHealthIcon';
import { ContentId } from 'src/utils/constants/contentId';
import { VENDOR_ACCOUNT_CREDENTIALS_ISSUE_TYPE_TO_CID } from 'src/utils/constants/contentIdMaps';
import {
  PurchaseVendorAccount,
  PurchaseVendorAccountInfo,
} from 'src/WebApiController';

import { IconButton } from '../Buttons';
import * as styles from './VendorAccountHealth.css';

export type VendorAccountHealthMode = 'full' | 'compact';

interface VendorAccountHealthProps {
  vendorAccount: PurchaseVendorAccountInfo | null | undefined;
  mode: VendorAccountHealthMode;
  onVendorAccountSaved?: () => void;
}

/**
 * Health affordance for a vendor account. `full` renders the "Update Password" CTA
 * (which carries the health glyph) when the account has a credentials issue, otherwise
 * the health icon; `compact` always renders the health icon. The icon opens the edit
 * vendor account dialog and shows a message-only tooltip in every state.
 */
export const VendorAccountHealth = ({
  vendorAccount,
  mode,
  onVendorAccountSaved,
}: VendorAccountHealthProps) => {
  const issueType = vendorAccount?.vendorAccountCredentialsIssueType ?? null;
  const hasIssue = issueType != null;
  const isHealthy = !hasIssue && Boolean(vendorAccount?.isAccountCrawlEnabled);

  const contentId = hasIssue
    ? VENDOR_ACCOUNT_CREDENTIALS_ISSUE_TYPE_TO_CID[issueType]
    : isHealthy
      ? ContentId.VendorAccountHealthy
      : ContentId.VendorAccountInactive;

  const fill = hasIssue
    ? vars.color.textError
    : isHealthy
      ? vars.color.textSuccess
      : vars.color.textDisabled;

  const handleVendorAccountSaved = useCallback(() => {
    onVendorAccountSaved?.();
  }, [onVendorAccountSaved]);

  const {
    onEditVendorAccount,
    venderAccountDialog,
    currentVendorAccount,
    onVendorAccountCancel,
    onSaveVendorAccount,
  } = useAddVendorAccountActions({
    onVendorAccountSaved: handleVendorAccountSaved,
  });

  const openEditDialog = useCallback(() => {
    if (!vendorAccount) {
      return;
    }
    onEditVendorAccount({
      id: vendorAccount.vendorAccountId,
      vendorId: vendorAccount.vendorId,
    } as PurchaseVendorAccount);
  }, [onEditVendorAccount, vendorAccount]);

  if (!vendorAccount) {
    return null;
  }

  const showCta = mode === 'full' && hasIssue;

  return (
    <>
      {showCta ? (
        <IconButton
          className={styles.updatePasswordButton}
          onClick={openEditDialog}
          icon={
            <>
              <VendorAccountHealthIcon
                size={vars.iconSize.xs}
                fill={vars.color.textError}
              />
              <Content id={ContentId.UpdatePassword} />
            </>
          }
        />
      ) : (
        <HoverableIcon
          icon={
            <IconButton
              className={styles.iconButton}
              onClick={openEditDialog}
              icon={
                <VendorAccountHealthIcon size={vars.iconSize.s} fill={fill} />
              }
            />
          }
          tooltip={<Content id={contentId} />}
        />
      )}
      {currentVendorAccount && (
        <PurchaseVendorAccountDialog
          {...venderAccountDialog.dialogProps}
          vendorAccountId={currentVendorAccount.id}
          vendorId={currentVendorAccount.vendorId}
          onClosed={onVendorAccountCancel}
          onSave={onSaveVendorAccount}
        />
      )}
    </>
  );
};
